import { LockOutlined, NumberOutlined, UserOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { Checkbox, Form, Input } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';
import { routes } from 'services/RouteService';
import AuthContainer, { AuthContainerProps } from '../containers/AuthContainer';
import { Button } from 'components/ui';
import { colors } from 'design';

export type LoginRegisterFormProps = AuthContainerProps & {
  askForLicenseCode?: boolean;
  displayCodeFull?: boolean;
  displayCodeExpired?: boolean;
  buttonTitle: string;
  buttonLoading: boolean;
  onButtonClick: (params: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    re_password?: string;
    license_code?: string;
  }) => void;
  passwordServerError?: string;
  initialValues?: {
    email?: string;
    re_email?: string;
    password?: string;
    re_password?: string;
    first_name?: string;
    last_name?: string;
    tc_and_privacy_policy_consent?: boolean;
    personal_information_access_consent?: boolean;
  };
};

const useStyles = createUseStyles({
  textInput: {
    fontSize: 16,
    textTransform: 'lowercase',
  },
  licenseCodeContainer: {
    marginBottom: 24,
  },
  codeFull: {
    color: colors.dataViz.bad,
    marginTop: 4,
    marginBottom: 0,
  },
  /** GYN-238: on narrow screens the label span steals flex space and `.ant-checkbox` shrinks (flex-shrink:1). Also top-align when the Form row would vertically center a tall wrapped label. */
  registerConsentForm: {
    '&& .ant-form-item-control-input': {
      alignItems: 'flex-start',
      minHeight: 0,
    },
    '&& .ant-checkbox-wrapper': {
      alignItems: 'flex-start',
      lineHeight: 1.5,
    },
    '&& .ant-checkbox': {
      flexShrink: 0,
      marginTop: '0.2em',
    },
  },
});

export default function LoginRegisterForm(props: LoginRegisterFormProps) {
  const {
    title,
    description,
    buttonTitle,
    mode,
    onButtonClick,
    buttonLoading,
    bottomLinkTo,
    askForLicenseCode,
    displayCodeFull,
    displayCodeExpired,
    passwordServerError,
    initialValues,
    onBackButtonClick,
  } = props;

  // Hooks
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const styles = useStyles();

  useEffect(() => {
    if (passwordServerError) {
      form.setFields([
        {
          name: 'password',
          errors: [t(`loginRegister.fields.password.errors.${passwordServerError}`)],
        },
      ]);
    }
  }, [form, passwordServerError, t]);

  const onFinish = (values: any) => {
    onButtonClick({
      email: values.email,
      password: values.password,
      ...(mode === 'register' && {
        first_name: values.first_name,
        last_name: values.last_name,
        license_code: values.license_code || undefined,
        re_password: values.re_password,
        tc_and_privacy_policy_consent: values.tc_and_privacy_policy_consent,
        personal_information_access_consent: values.personal_information_access_consent,
        email_notifications_consent: !(Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android'),
      }),
    });
  };

  const inlineStyles = {
    passwordResetAction: {
      marginBottom: 24,
      textAlign: 'right' as const,
    },
    passwordResetActionLink: {
      textDecoration: 'underline',
    },
  };

  const validateEmail = (rule: any, value: any, callback: any) => {
    const emailRegex = /^[A-Za-z0-9+_.-]+@(.+)$/;
    if (!emailRegex.test(value)) {
      callback(t('loginRegister.fields.email.errors.notEmail'));
    } else {
      callback();
    }
  };

  const validatePassword = (rule: any, value: any, callback: any) => {
    if (!value) {
      callback();
      return;
    }

    const hasMinLength = value.length >= 12;
    const hasLowercase = /[a-z]/.test(value);
    const hasUppercase = /[A-Z]/.test(value);
    const hasDigit = /\d/.test(value);

    if (hasMinLength && hasLowercase && hasUppercase && hasDigit) {
      callback();
    } else {
      callback(t('loginRegister.fields.password.errors.requirements'));
    }
  };

  return (
    <AuthContainer
      title={title}
      description={description}
      mode={mode}
      bottomLinkTo={bottomLinkTo}
      displayBackButton
      onBackButtonClick={onBackButtonClick}
    >
      <Form
        form={form}
        name="normal_login"
        onFinish={onFinish}
        layout="vertical"
        requiredMark="optional"
        className={mode === 'register' ? styles.registerConsentForm : undefined}
        initialValues={initialValues}
      >
        <Form.Item
          name="email"
          rules={[{ required: true }, { validator: validateEmail }]}
          style={mode === 'register' ? { marginBottom: 5 } : undefined}
        >
          <Input
            prefix={<UserOutlined />}
            autoCapitalize="none"
            placeholder={t('loginRegister.fields.email.placeholder')}
            allowClear
            className={styles.textInput}
            autoComplete="username"
          />
        </Form.Item>

        {mode === 'register' && (
          <Form.Item
            name="re_email"
            dependencies={['email']}
            rules={[
              {
                required: true,
                message: t('loginRegister.fields.emailConfirmation.errors.required'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('email') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('loginRegister.fields.emailConfirmation.errors.emailsNotMatch')));
                },
              }),
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              autoCapitalize="none"
              placeholder={t('loginRegister.fields.emailConfirmation.placeholder')}
              allowClear
              className={styles.textInput}
              autoComplete="username"
            />
          </Form.Item>
        )}

        <Form.Item
          name="password"
          rules={[{ required: true }, ...(mode === 'register' ? [{ validator: validatePassword }] : [])]}
          style={{ marginBottom: 5 }}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoCapitalize="none"
            placeholder={t('loginRegister.fields.password.placeholder')}
            allowClear
            className={styles.textInput}
            autoComplete="current-password"
          />
        </Form.Item>
        {mode === 'login' && (
          <div style={inlineStyles.passwordResetAction}>
            <Link to={routes.passwordResetSendEmail.route} style={inlineStyles.passwordResetActionLink}>
              {t('loginRegister.actions.passwordReset')}
            </Link>
          </div>
        )}

        {mode === 'register' && (
          <Form.Item
            name="re_password"
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: t('loginRegister.fields.passwordConfirmation.errors.required'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t('loginRegister.fields.passwordConfirmation.errors.passwordsNotMatch')),
                  );
                },
              }),
            ]}
          >
            <Input.Password
              autoCapitalize="none"
              prefix={<LockOutlined />}
              placeholder={t('loginRegister.fields.passwordConfirmation.placeholder')}
              allowClear
              className={styles.textInput}
              autoComplete="current-password"
            />
          </Form.Item>
        )}

        {mode === 'register' && (
          <Form.Item
            name="first_name"
            rules={[{ required: true, message: t('loginRegister.fields.firstName.errors.required') }]}
            style={{ marginBottom: 5 }}
          >
            <Input placeholder={t('loginRegister.fields.firstName.placeholder')} allowClear />
          </Form.Item>
        )}
        {mode === 'register' && (
          <Form.Item
            name="last_name"
            rules={[{ required: true, message: t('loginRegister.fields.lastName.errors.required') }]}
          >
            <Input placeholder={t('loginRegister.fields.lastName.placeholder')} allowClear />
          </Form.Item>
        )}
        {mode === 'register' && askForLicenseCode && (
          <div className={styles.licenseCodeContainer}>
            <p style={{ color: colors.text.tertiary, marginBottom: 4 }}>
              {t('loginRegister.fields.licenseCode.description')}
            </p>
            <Form.Item name="license_code" style={{ marginBottom: 4 }}>
              <Input
                prefix={<NumberOutlined />}
                placeholder={t('loginRegister.fields.licenseCode.placeholder')}
                allowClear
                className={styles.textInput}
              />
            </Form.Item>
            {displayCodeFull && <p className={styles.codeFull}>{t('loginRegister.fields.licenseCode.full')}</p>}
            {displayCodeExpired && <p className={styles.codeFull}>{t('loginRegister.fields.licenseCode.expired')}</p>}
          </div>
        )}
        {mode === 'register' && (
          <Form.Item
            name="tc_and_privacy_policy_consent"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t('loginRegister.fields.tc_and_privacy_policy_consent.errors.required')),
                      ),
              },
            ]}
            style={{ marginBottom: 10 }}
            valuePropName="checked"
          >
            <Checkbox>
              <Link to={t('legal.termsAndPrivacy')} target="_blank" style={{ textDecoration: 'underline' }}>
                {t('loginRegister.fields.tc_and_privacy_policy_consent.title')}
              </Link>{' '}
              *
            </Checkbox>
          </Form.Item>
        )}
        {mode === 'register' && (
          <Form.Item
            name="personal_information_access_consent"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t('loginRegister.fields.personal_information_access_consent.errors.required')),
                      ),
              },
            ]}
            style={{ marginBottom: 10 }}
            valuePropName="checked"
          >
            <Checkbox>{t('loginRegister.fields.personal_information_access_consent.title')} *</Checkbox>
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: '0px' }}>
          <Button variant="primary" block size="lg" htmlType="submit" disabled={buttonLoading} loading={buttonLoading}>
            {buttonTitle}
          </Button>
        </Form.Item>
      </Form>
    </AuthContainer>
  );
}
